/**
 * HAObject — racine état/données (ESM, ha/440).
 *
 * Conversion namespace→ESM. Imports explicites pour `toMap` (helper HA) et
 * `ServiceManager` (leaf tightement couplé, instancié dans `initialize`).
 *
 * Le type `Tag` du legacy (`HA.Core.HTML.Tag|HA.Core.HTML.Container| any`) est
 * réduit à `unknown` ici pour éviter un cycle d'import HAObject→Tag→HTML→HAObject.
 * `getTag()` retourne `unknown` ; les call-sites typés (Tag) font le narrow
 * eux-mêmes. Le `instanceof HA.Core.HTML.Tag` du legacy devient un duck-typing
 * sur `getElement` (méthode commune Tag/Container) — équivalent fonctionnel,
 * plus simple à factoriser pour ha/441.
 */

import { toMap } from "../../HA";

// ── Cycle ESM ServiceManager ↔ HAObject (ha/440) ──────────────────────────
// ServiceManager extends HAObject ; HAObject instancie ServiceManager dans
// `initialize`. En ESM, importer ServiceManager statiquement ici crée un TDZ
// (le module ServiceManager s'évalue avant que `HAObject` ne soit lié) →
// `class extends undefined`. Solution : ServiceManager se RÉGISTRE auprès de
// HAObject à son top-level via `registerServiceManager(ctor)`. La constante
// `_ServiceManagerCtor` est ainsi peuplée AVANT le premier `new HAObject({
// services: ... })` (qui arrive en runtime, après tous les imports).
let _ServiceManagerCtor: (new (data?: any) => HAObject) | undefined;
export function registerServiceManager(ctor: new (data?: any) => HAObject): void {
    _ServiceManagerCtor = ctor;
}

// Même pattern pour `Tag` : `getElement()` doit reproduire l'`instanceof
// HA.Core.HTML.Tag` du legacy sans créer un cycle d'import HAObject↔Tag.
// Tag s'enregistre au top-level de son module. Si non enregistré (cluster
// minimal type-only), l'`instanceof` est skipped → fallback équivalent au
// chemin legacy où le tag n'est pas un Tag (la branche descend).
let _TagCtor: Function | undefined;
export function registerTagCtor(ctor: Function): void {
    _TagCtor = ctor;
}

export class HAObject {
    protected data: Map<string, any> | undefined;

    public constructor(data?: any) {
        this.initialize(data);
    }

    public getElement(useTag?: boolean): Element | any {
        let element = this.get("element");
        if (!element) {
            const tag = this.getTag();
            // Sémantique LEGACY exacte (codex finding ha/440 t.2) :
            //   tag && (useTag || !(tag instanceof HA.Core.HTML.Tag))
            // Le `_TagCtor` est rempli par Tag.ts via registerTagCtor() au
            // top-level (cycle ESM cassé). Si non enregistré → on traite tag
            // comme « pas un Tag » (équivalent à l'absence de la classe en
            // build minimal type-only).
            if (tag && (useTag || !(_TagCtor && tag instanceof (_TagCtor as any)))) {
                element = (tag as { getElement: () => Element }).getElement();
            }
        }
        return element;
    }

    public getTag(): unknown {
        return this.get("tag");
    }

    public setTag(tag: unknown): Map<string, any> {
        return this.addToData("tag", tag);
    }

    protected get(id: any): any {
        return this.getData().get(id);
    }

    protected remove(id: any): void {
        this.getData().delete(id);
    }

    protected has(id: any): boolean {
        return this.getData().has(id);
    }

    protected hasNot(id: any): boolean {
        return !this.has(id);
    }

    protected getData(): Map<any, any> {
        if (!this.data) {
            this.data = new Map();
        }
        return this.data;
    }

    protected getServiceManager(): HAObject {
        return this.get("services");
    }

    protected updateData(data: Map<any, any>): void {
        data.forEach((v, k) => {
            this.addToData(k, v);
        });
    }

    protected addToData(name: any, data: any): Map<string, any> {
        return this.getData().set(name, data);
    }

    protected initialize(data?: any): void {
        if (data instanceof Map) {
            this.data = data;
        } else if (data instanceof Object) {
            this.data = toMap(data) as Map<string, any>;
        }

        const services = this.get("services");
        // Si services présent et pas déjà wrappé par le ctor enregistré → wrap.
        // (Détection sans `instanceof ServiceManager` direct pour casser le cycle
        // ESM ; on compare au ctor enregistré au top-level via registerServiceManager.)
        if (services && _ServiceManagerCtor && !(services instanceof _ServiceManagerCtor)) {
            this.addToData("services", new _ServiceManagerCtor(toMap(services)));
        }
    }

    protected addServices(services: Map<string, () => void>): unknown {
        return (this.getServiceManager() as any).addServices(services);
    }

    protected addService(serviceName: string, func: () => void): unknown {
        return (this.getServiceManager() as any).addService(serviceName, func);
    }

    protected getServices(names: string[]): Map<string, () => void> {
        return (this.getServiceManager() as any).getServices(names);
    }

    /**
     * Note ha/440 : legacy laissait des sous-classes (Service.App) overrider
     * cette méthode avec une signature différente (lecture par clé dans la Map
     * data). Type de retour `any` pour préserver cette latitude — sinon TS
     * refuse l'override sous strict mode ESM. ha/441 pourra unifier.
     */
    protected getService(name: string): any {
        return (this.getServiceManager() as any).getService(name);
    }
}
